/**
 * Zapis stroju: lustro SaveLocal natychmiast + PUT /api/profile/outfit z debounce 1 s.
 */
import { OutfitStore } from '../../avatar/outfitStore.js';
import { putOutfit } from '../../avatar/api.js';
import {
  serializeOutfitSlots,
  serializeSwimSlots,
  type OutfitSlots,
  type SwimSlots,
} from '../../avatar/parseOutfit.js';

export const OUTFIT_SAVE_DEBOUNCE_MS = 1000;

export type OutfitPersistState = {
  slots: OutfitSlots;
  swimSlots: SwimSlots;
  version: number;
  saving: boolean;
  lastError: string | null;
  /** timestamp ostatniego schedule */
  scheduledAt: number | null;
};

export type OutfitPersistTimers = {
  setTimeout: (fn: () => void, ms: number) => ReturnType<typeof setTimeout>;
  clearTimeout: (id: ReturnType<typeof setTimeout>) => void;
};

const defaultTimers: OutfitPersistTimers = {
  setTimeout: (fn, ms) => setTimeout(fn, ms),
  clearTimeout: (id) => clearTimeout(id),
};

export type OutfitPersistController = {
  /** Natychmiast lustro lokalne + zaplanuj PUT. */
  schedule(slots: OutfitSlots, swimSlots: SwimSlots): void;
  /**
   * Wymuś zapis teraz (anuluje debounce).
   * Czeka na aktywny PUT i — jeśli stan jest nowszy/dirty — wysyła go ponownie.
   * Promise rozwiązuje się dopiero po wysłaniu najnowszego stanu.
   */
  flush(): Promise<void>;
  getState(): OutfitPersistState;
  setVersion(v: number): void;
  destroy(): void;
};

/**
 * Kontroler debounced save. `onStatus` — komunikaty PL do UI.
 */
export function createOutfitPersist(opts: {
  version: number;
  onStatus?: (msg: string) => void;
  onVersion?: (v: number) => void;
  timers?: OutfitPersistTimers;
  debounceMs?: number;
}): OutfitPersistController {
  const timers = opts.timers ?? defaultTimers;
  const debounceMs = opts.debounceMs ?? OUTFIT_SAVE_DEBOUNCE_MS;
  let version = opts.version;
  let slots: OutfitSlots | null = null;
  let swimSlots: SwimSlots | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let saving = false;
  let lastError: string | null = null;
  let scheduledAt: number | null = null;
  let destroyed = false;
  /** Potrzeba kolejnego PUT z najnowszym stanem (np. zmiana podczas trwającego zapisu). */
  let dirty = false;
  /** Wspólny łańcuch aktywnego drainu PUT — waiters czekają na pełne opróżnienie. */
  let flight: Promise<void> | null = null;

  const mirrorLocal = (s: OutfitSlots, sw: SwimSlots): void => {
    OutfitStore.saveSlots(s);
    OutfitStore.saveSwimSlots(sw);
  };

  const handlePutResult = async (): Promise<void> => {
    if (!slots || !swimSlots) return;
    lastError = null;
    opts.onStatus?.('Zapisuję…');
    const body = {
      slots: serializeOutfitSlots(slots),
      swimSlots: serializeSwimSlots(swimSlots),
      version,
    };
    const res = await putOutfit(body);
    if (destroyed) return;

    if (res.ok) {
      version = res.data.version;
      OutfitStore.commitOutfit(slots, swimSlots, version);
      opts.onVersion?.(version);
      opts.onStatus?.('Zapisano! 💖');
      return;
    }

    mirrorLocal(slots, swimSlots);
    if (res.code === 'VERSION_CONFLICT') {
      lastError = 'VERSION_CONFLICT';
      opts.onStatus?.('Odświeżam… spróbuj znów');
      return;
    }
    if (res.code === 'UNAUTHORIZED' || res.status === 0) {
      lastError = res.code;
      opts.onStatus?.('Zapis lokalny (offline)');
      return;
    }
    if (res.code === 'VALIDATION') {
      lastError = 'VALIDATION';
      opts.onStatus?.('Najpierw kup ubranko w sklepie');
      return;
    }
    lastError = res.code;
    opts.onStatus?.('Spróbuj później');
  };

  /**
   * Drain PUT: jeśli nie ma aktywnego drainu i dirty — odpala pętlę.
   * Kolejne wywołania bez nowej zmiany (dirty=false) tylko czekają na istniejący flight
   * — nie wymuszają kolejnego PUT.
   * Zwraca Promise, które rozwiązuje się po opróżnieniu dirty (najnowszy stan wysłany).
   */
  const doPut = (): Promise<void> => {
    if (destroyed) return Promise.resolve();
    if (!flight) {
      // Brak pending zmian i brak lotu — nic do wysłania (idempotentny flush).
      if (!dirty) return Promise.resolve();
      flight = (async () => {
        while (dirty && !destroyed) {
          dirty = false;
          if (!slots || !swimSlots) break;
          saving = true;
          try {
            await handlePutResult();
          } finally {
            saving = false;
          }
        }
      })().finally(() => {
        flight = null;
      });
    }
    // Waiter musi dołączyć ewentualny follow-up, jeśli dirty wstało w luce po drainie.
    return flight.then(() => {
      if (dirty && !destroyed) {
        return doPut();
      }
    });
  };

  return {
    schedule(s, sw) {
      if (destroyed) return;
      slots = s;
      swimSlots = sw;
      mirrorLocal(s, sw);
      // dirty tylko ze schedule: stan zmieniony / czeka na (pierwszy) zapis.
      dirty = true;
      if (timer) timers.clearTimeout(timer);
      scheduledAt = Date.now();
      timer = timers.setTimeout(() => {
        timer = null;
        void doPut();
      }, debounceMs);
    },
    async flush() {
      if (destroyed) return;
      if (timer) {
        timers.clearTimeout(timer);
        timer = null;
      }
      if (!slots || !swimSlots) return;
      // Bez nowej zmiany: dołącza do flight lub no-op — nie ustawia dirty.
      await doPut();
    },
    getState() {
      return {
        slots: slots ?? OutfitStore.load().slots,
        swimSlots: swimSlots ?? OutfitStore.load().swimSlots,
        version,
        saving,
        lastError,
        scheduledAt,
      };
    },
    setVersion(v) {
      version = v;
      OutfitStore.saveVersion(v);
    },
    destroy() {
      destroyed = true;
      if (timer) {
        timers.clearTimeout(timer);
        timer = null;
      }
    },
  };
}

/**
 * Teardown: await flush() (w tym follow-up PUT po dirty), potem destroy().
 * Bezpieczne wywołanie przy wyjściu ze sceny (Shop / Wardrobe).
 */
export function disposeOutfitPersist(
  persist:
    | Pick<OutfitPersistController, 'flush' | 'destroy'>
    | null
    | undefined,
): void {
  if (!persist) return;
  void Promise.resolve(persist.flush()).finally(() => {
    persist.destroy();
  });
}
